import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";
import { Mark } from "../../components/site/Icons";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const EMPTY = {
  title: "", subtitle: "", duration: "1-day", location: "Srisailam, Andhra Pradesh",
  price: 0, price_note: "per person", note: "", departure: "", hero_image: "", overview: "",
  highlights: [], inclusions: [], exclusions: [], gallery: [], itinerary: [],
  featured: false, active: true, order: 0,
};

const linesToArr = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const arrToLines = (a) => (a || []).join("\n");
const itinToText = (a) => (a || []).map((i) => `${i.time} | ${i.title} | ${i.description}`).join("\n");
const textToItin = (s) => linesToArr(s).map((l) => {
  const [time = "", title = "", description = ""] = l.split("|").map((x) => x.trim());
  return { time, title, description };
});

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [trips, setTrips] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [text, setText] = useState({ highlights: "", inclusions: "", exclusions: "", gallery: "", itinerary: "" });

  const loadAll = useCallback(async () => {
    const [t, e, s] = await Promise.all([
      api.get("/admin/trips"), api.get("/admin/enquiries"), api.get("/settings"),
    ]);
    setTrips(t.data); setEnquiries(e.data); setSettings(s.data);
  }, []);

  useEffect(() => {
    api.get("/auth/me").then(() => { setReady(true); loadAll(); })
      .catch(() => navigate("/admin/login"));
  }, [navigate, loadAll]);

  const logout = () => { localStorage.removeItem("rsk_token"); navigate("/admin/login"); };

  const openNew = () => {
    setEditing(null); setForm(EMPTY);
    setText({ highlights: "", inclusions: "", exclusions: "", gallery: "", itinerary: "" });
    setDialogOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t); setForm({ ...EMPTY, ...t });
    setText({
      highlights: arrToLines(t.highlights), inclusions: arrToLines(t.inclusions),
      exclusions: arrToLines(t.exclusions), gallery: arrToLines(t.gallery), itinerary: itinToText(t.itinerary),
    });
    setDialogOpen(true);
  };

  const saveTrip = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    const payload = {
      ...form, price: Number(form.price), order: Number(form.order),
      highlights: linesToArr(text.highlights), inclusions: linesToArr(text.inclusions),
      exclusions: linesToArr(text.exclusions), gallery: linesToArr(text.gallery),
      itinerary: textToItin(text.itinerary),
    };
    try {
      if (editing) await api.put(`/admin/trips/${editing.id}`, payload);
      else await api.post("/admin/trips", payload);
      toast.success(editing ? "Trip updated" : "Trip created");
      setDialogOpen(false); loadAll();
    } catch { toast.error("Save failed"); }
  };

  const deleteTrip = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    await api.delete(`/admin/trips/${t.id}`); toast.success("Deleted"); loadAll();
  };

  const setEnqStatus = async (id, status) => {
    await api.put(`/admin/enquiries/${id}`, { status }); loadAll();
  };
  const deleteEnq = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/admin/enquiries/${id}`); loadAll();
  };

  const saveSettings = async () => {
    try { await api.put("/admin/settings", settings); toast.success("Settings saved"); }
    catch { toast.error("Save failed"); }
  };

  if (!ready) return <div className="min-h-screen flex items-center justify-center text-muted2">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#F0EDEB]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-border sticky top-0 z-20">
        <div className="max-w-[1300px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mark className="h-9 w-auto rounded-sm" />
            <span className="font-heading text-xl">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm text-muted2 hover:text-obsidian inline-flex items-center gap-1.5" data-testid="admin-view-site">
              <ExternalLink className="w-4 h-4" /> View site
            </a>
            <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout" className="rounded-full">
              <LogOut className="w-4 h-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1300px] mx-auto px-5 sm:px-8 py-10">
        <Tabs defaultValue="trips">
          <TabsList className="mb-8">
            <TabsTrigger value="trips" data-testid="tab-trips">Trips ({trips.length})</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries ({enquiries.length})</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          {/* TRIPS */}
          <TabsContent value="trips">
            <div className="flex justify-between items-center mb-5">
              <h2 className="font-heading text-3xl">Manage journeys</h2>
              <Button onClick={openNew} data-testid="add-trip-btn" className="bg-saffron hover:bg-saffron-hover text-bone rounded-full">
                <Plus className="w-4 h-4 mr-1.5" /> New trip
              </Button>
            </div>
            <div className="bg-white border border-border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead><TableHead>Duration</TableHead><TableHead>Price</TableHead>
                    <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trips.map((t) => (
                    <TableRow key={t.id} data-testid={`admin-trip-row-${t.slug}`}>
                      <TableCell className="font-medium">{t.title}</TableCell>
                      <TableCell>{t.duration === "2-day" ? "2 Days" : "1 Day"}</TableCell>
                      <TableCell>₹{t.price}</TableCell>
                      <TableCell>
                        {t.active ? <Badge className="bg-green-600">Active</Badge> : <Badge variant="secondary">Hidden</Badge>}
                        {t.featured && <Badge variant="outline" className="ml-1 border-saffron text-saffron">Featured</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`edit-trip-${t.slug}`}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteTrip(t)} data-testid={`delete-trip-${t.slug}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* ENQUIRIES */}
          <TabsContent value="enquiries">
            <h2 className="font-heading text-3xl mb-5">Enquiries</h2>
            <div className="bg-white border border-border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Trip</TableHead>
                    <TableHead>Boarding</TableHead>
                    <TableHead>Pax</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enquiries.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted2 py-10">No enquiries yet</TableCell></TableRow>}
                  {enquiries.map((e) => (
                    <TableRow key={e.id} data-testid={`enquiry-row-${e.id}`}>
                      <TableCell className="font-medium">{e.name}</TableCell>
                      <TableCell>{e.phone}</TableCell>
                      <TableCell>{e.trip_title || "—"}</TableCell>
                      <TableCell>{e.boarding_point || "—"}</TableCell>
                      <TableCell>{e.travellers}</TableCell>
                      <TableCell>{e.preferred_date || "—"}</TableCell>
                      <TableCell>
                        <Select value={e.status} onValueChange={(v) => setEnqStatus(e.id, v)}>
                          <SelectTrigger className="h-8 w-28" data-testid={`enquiry-status-${e.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="booked">Booked</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell><Button variant="ghost" size="icon" onClick={() => deleteEnq(e.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="settings">
            <h2 className="font-heading text-3xl mb-5">Site settings</h2>
            {settings && (
              <div className="bg-white border border-border rounded-md p-8 max-w-2xl space-y-5">
                {[
                  ["agency_name", "Agency name"], ["tagline", "Tagline"],
                  ["contact_name", "Contact name (owner)"],
                  ["whatsapp_number", "WhatsApp number (digits, with country code e.g. 916302206106)"],
                  ["phone", "Phone"], ["email", "Email"], ["address", "Address"],
                ].map(([k, label]) => (
                  <div key={k}>
                    <Label>{label}</Label>
                    <Input value={settings[k] || ""} onChange={(e) => setSettings({ ...settings, [k]: e.target.value })} className="mt-1.5" data-testid={`setting-${k}`} />
                  </div>
                ))}
                <Button onClick={saveSettings} data-testid="save-settings-btn" className="bg-saffron hover:bg-saffron-hover text-bone rounded-full">Save settings</Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Trip dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="trip-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Edit trip" : "New trip"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="trip-title-input" /></div>
              <div><Label>Duration</Label>
                <Select value={form.duration} onValueChange={(v) => setForm({ ...form, duration: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="trip-duration-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="1-day">1 Day</SelectItem><SelectItem value="2-day">2 Day</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Subtitle</Label><Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Departure city</Label><Input value={form.departure} onChange={(e) => setForm({ ...form, departure: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Price (₹)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1.5" data-testid="trip-price-input" /></div>
              <div><Label>Price note</Label><Input value={form.price_note} onChange={(e) => setForm({ ...form, price_note: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Hero image URL</Label><Input value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Note (small caveat shown under price, optional)</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Overview</Label><Textarea value={form.overview} onChange={(e) => setForm({ ...form, overview: e.target.value })} rows={3} className="mt-1.5" /></div>
            <div><Label>Highlights (one per line)</Label><Textarea value={text.highlights} onChange={(e) => setText({ ...text, highlights: e.target.value })} rows={3} className="mt-1.5" /></div>
            <div><Label>Itinerary (one per line: time | title | description)</Label><Textarea value={text.itinerary} onChange={(e) => setText({ ...text, itinerary: e.target.value })} rows={4} className="mt-1.5 font-mono text-xs" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Inclusions (one per line)</Label><Textarea value={text.inclusions} onChange={(e) => setText({ ...text, inclusions: e.target.value })} rows={4} className="mt-1.5" /></div>
              <div><Label>Exclusions (one per line)</Label><Textarea value={text.exclusions} onChange={(e) => setText({ ...text, exclusions: e.target.value })} rows={4} className="mt-1.5" /></div>
            </div>
            <div><Label>Gallery image URLs (one per line)</Label><Textarea value={text.gallery} onChange={(e) => setText({ ...text, gallery: e.target.value })} rows={3} className="mt-1.5" /></div>
            <div className="flex items-center gap-8 pt-2">
              <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="trip-active-switch" /><Label>Active</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} /><Label>Featured</Label></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={saveTrip} data-testid="save-trip-btn" className="bg-saffron hover:bg-saffron-hover text-bone rounded-full">Save trip</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
